/**
 * 解析互动消息未读 SSE / 文本流片段，兼容：
 * - 标准 SSE：data: {"unreadCount":1}
 * - 自定义：unread{"unreadCount":0}
 */
export function parseUnreadCountFromText(text) {
  if (!text || typeof text !== 'string') return null

  const tryJson = (s) => {
    try {
      const j = JSON.parse(s)
      if (j && j.unreadCount !== undefined && j.unreadCount !== null) {
        const n = Number(j.unreadCount)
        return Number.isNaN(n) ? null : n
      }
    } catch {
      /* ignore */
    }
    return null
  }

  const t = text.trim()
  if (!t) return null

  if (t.startsWith('data:')) {
    return tryJson(t.slice(5).trim())
  }

  const m = t.match(/unread\s*(\{[\s\S]*\})/)
  if (m) {
    const n = tryJson(m[1])
    if (n !== null) return n
  }

  const m2 = t.match(/\{[\s\S]*"unreadCount"[\s\S]*\}/)
  if (m2) {
    return tryJson(m2[0])
  }

  return null
}

/**
 * 从一段文本中抠出所有 unread{...} 片段并回调，返回去掉这些片段后的剩余字符串（用于无换行粘包）
 */
export function stripUnreadJsonFragments(text, onUnreadCount) {
  if (!text) return ''
  return text.replace(/unread\s*(\{[^}]+\})/g, (full) => {
    const n = parseUnreadCountFromText(full)
    if (n !== null) onUnreadCount(n)
    return ''
  })
}

/**
 * 使用 fetch 读取 GET SSE 流（携带 COMIC-WEB-TOKEN）
 * @returns {() => void} abort 停止当前请求
 */
export function startInteractionUnreadStream({
  url,
  token,
  onUnreadCount,
  onError,
  onEnd,
}) {
  const ac = new AbortController()

  const run = async () => {
    let lineBuf = ''
    try {
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          'COMIC-WEB-TOKEN': token,
          Accept: 'text/event-stream',
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
          'Accept-Encoding': 'identity',
        },
        signal: ac.signal,
        cache: 'no-store',
      })
      if (!res.ok || !res.body) {
        onError?.(new Error(`SSE HTTP ${res.status}`))
        return
      }
      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        lineBuf += decoder.decode(value, { stream: true })

        const parts = lineBuf.split(/\n/)
        lineBuf = parts.pop() ?? ''
        for (const raw of parts) {
          const line = raw.replace(/\r$/, '')
          const afterUnread = stripUnreadJsonFragments(line, onUnreadCount)
          const n = parseUnreadCountFromText(afterUnread.trim())
          if (n !== null) onUnreadCount(n)
        }

        lineBuf = stripUnreadJsonFragments(lineBuf, onUnreadCount)
        const tailParsed = parseUnreadCountFromText(lineBuf.trim())
        if (tailParsed !== null) {
          onUnreadCount(tailParsed)
          lineBuf = ''
        }

        if (lineBuf.length > 65536) {
          lineBuf = lineBuf.slice(-32768)
        }
      }
      const afterUnread = stripUnreadJsonFragments(lineBuf, onUnreadCount)
      const tail = parseUnreadCountFromText(afterUnread.trim())
      if (tail !== null) onUnreadCount(tail)
      onEnd?.()
    } catch (e) {
      if (e?.name === 'AbortError') return
      onError?.(e)
    }
  }

  run()
  return () => ac.abort()
}
